/**
 * Callable `getSignedUpPatientInviteContext` — public preview for an invitation.
 *
 * Resolvable either by the `signedUpPatients` document id or by the short invite code
 * (`shortId`) from `/o/{shortId}` links (PF-5075). Short-link resolution returns the
 * resolved `signedUpPatientId` in `data` so the caller can continue with the regular
 * `?s=&u=` onboarding flow.
 *
 * See `docs/PF-4846/dependencies-and-references.md`.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebaseConfig';

export type GetSignedUpPatientInviteContextRequest = {
  signedUpPatientId?: string;
  shortId?: string;
  linkUtmCode?: string;
};

export type GetSignedUpPatientInviteContextData = {
  signedUpPatientId: string;
  practiceName: string;
  therapistFirstName: string;
  therapistLastName: string;
  therapistImageUrl?: string;
  courseName: string;
  courseId?: string;
  /** Practice logo (Cloudflare Images URL). Present only when the practice has the branding package. */
  practiceLogoUrl?: string;
  /** Practice charges private patients a fee for app use (PF-5963). Absent from older function versions. */
  practiceChargesPrivateFee?: boolean;
};

export type GetSignedUpPatientInviteContextResponse =
  | { status: 'success'; notFound: true; data?: undefined }
  | { status: 'success'; notFound: false; data: GetSignedUpPatientInviteContextData }
  | { status: 'error'; message: string };

const callable = httpsCallable<
  GetSignedUpPatientInviteContextRequest,
  GetSignedUpPatientInviteContextResponse
>(functions, 'getSignedUpPatientInviteContext');

const inFlight = new Map<string, Promise<GetSignedUpPatientInviteContextResponse>>();

function callDeduped(
  dedupeKey: string,
  request: GetSignedUpPatientInviteContextRequest,
): Promise<GetSignedUpPatientInviteContextResponse> {
  const existing = inFlight.get(dedupeKey);
  if (existing) {
    return existing;
  }
  const promise = callable(request)
    .then((r) => r.data)
    .finally(() => {
      inFlight.delete(dedupeKey);
    });
  inFlight.set(dedupeKey, promise);
  return promise;
}

export function fetchSignedUpPatientInviteContext(
  signedUpPatientId: string,
  linkUtmCode?: string,
): Promise<GetSignedUpPatientInviteContextResponse> {
  const trimmedUtm = linkUtmCode?.trim();
  const request: GetSignedUpPatientInviteContextRequest = { signedUpPatientId };
  if (trimmedUtm) {
    request.linkUtmCode = trimmedUtm;
  }
  return callDeduped(signedUpPatientId, request);
}

/** Resolves a short invite code (`/o/{shortId}`) to invite context, including the document id. */
export function fetchSignedUpPatientInviteContextByShortId(
  shortId: string,
  linkUtmCode?: string,
): Promise<GetSignedUpPatientInviteContextResponse> {
  const trimmedUtm = linkUtmCode?.trim();
  const request: GetSignedUpPatientInviteContextRequest = { shortId };
  if (trimmedUtm) {
    request.linkUtmCode = trimmedUtm;
  }
  return callDeduped(`short:${shortId}`, request);
}
